import { Component, OnInit, inject, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { TreeSelectModule } from 'primeng/treeselect';
import { BackButton } from '@components/ui/back-button/back-button';
import { CustomPaginatorComponent } from '@root/src/app/pages/Ui-Kit/custom-paginator/custom-paginator.component';
import { AiFieldBoundaryService } from '@6grain/masofa-monolith-api-client/ai-inference/api/aiFieldBoundary.service';
import { AiFieldBoundaryDto } from '@6grain/masofa-monolith-api-client/ai-inference/model/aiFieldBoundaryDto';
import { SortType } from '@6grain/masofa-monolith-api-client/ai-inference';
import { Subject, forkJoin, of } from 'rxjs';
import { takeUntil, finalize, catchError } from 'rxjs/operators';
import { PaginatorState } from 'primeng/paginator';
import { FieldBoundaryModalComponent } from '../field-boundary-modal/field-boundary-modal.component';
import { RegionManagerService } from '@app/components/openstreetmap/managers/region-manager.service';
import { RegionService } from '@6grain/masofa-monolith-api-client/dictionaries/api/region.service';
import { FieldFilter, Region } from '@6grain/masofa-monolith-api-client/dictionaries';
import { ServerTranslatePipe } from '@app/utils/pipes/server-translate-pipe';

@Component({
  selector: 'app-fields-identification-list',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    TableModule,
    BackButton,
    CustomPaginatorComponent,
    ButtonModule,
    FieldBoundaryModalComponent,
    FormsModule,
    DatePickerModule,
    TreeSelectModule,
    ServerTranslatePipe,
  ],
  templateUrl: './fields-identification-list.component.html',
  styleUrls: ['./fields-identification-list.component.scss'],
})
export class FieldsIdentificationListComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly aiFieldService = inject(AiFieldBoundaryService);
  private readonly regionManager = inject(RegionManagerService);
  private readonly regionService = inject(RegionService);
  private readonly cdr = inject(ChangeDetectorRef);

  boundaries: AiFieldBoundaryDto[] = [];
  regions: Region[] = [];
  isLoading = false;

  totalCount = 0;
  pageSize = 20;
  first = 0;

  displayModal = false;
  selectedField: AiFieldBoundaryDto | null = null;

  regionNodes: any[] = [];
  selectedRegion: any = null;
  dateRange: Date[] | null = null;

  ngOnInit(): void {
    this.loadRegionTree();
    this.loadRegionsDictionary();
    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async loadRegionTree() {
    try {
      const rawNodes = await this.regionManager.getRegionTree();
      this.regionNodes = this.mapToPrimeNgTree(rawNodes);
    } catch (e) {
      console.error('Failed to load region tree', e);
    }
  }

  private loadRegionsDictionary(): void {
    this.regionService
      .dictionariesRegionGetByQueryPost({})
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.regions = res || [];
      });
  }

  private mapToPrimeNgTree(nodes: any[]): any[] {
    return nodes.map((node) => ({
      label: node.name,
      data: node,
      key: node.id,
      children: node.children ? this.mapToPrimeNgTree(node.children) : [],
    }));
  }

  getRegionDisplayInfo(regionId: string | undefined): { region: Region | null; district: Region | null } {
    if (!regionId || !this.regions.length) {
      return { region: null, district: null };
    }
    const currentRegion = this.regions.find((r) => r.id === regionId);
    if (!currentRegion) {
      return { region: null, district: null };
    }
    if (currentRegion.parentId && currentRegion.parentId !== currentRegion.id) {
      const parentRegion = this.regions.find((r) => r.id === currentRegion.parentId);
      if (parentRegion) {
        return { region: parentRegion, district: currentRegion };
      }
    }
    return { region: currentRegion, district: null };
  }

  applyFilters(): void {
    this.first = 0;
    this.loadData();
  }

  resetFilters(): void {
    this.selectedRegion = null;
    this.dateRange = null;
    this.first = 0;
    this.loadData();
  }

  async loadData(): Promise<void> {
    this.isLoading = true;

    const filters: FieldFilter[] = [];
    let regId: string | null = null;

    if (this.selectedRegion) {
      regId = this.selectedRegion.key || (this.selectedRegion.data && this.selectedRegion.data.id);
      if (regId) {
        filters.push({
          filterField: 'regionId',
          filterValue: regId,
          filterOperator: 0,
        });
      }
    }

    if (this.dateRange && this.dateRange.length > 0) {
      if (this.dateRange[0]) {
        filters.push({
          filterField: 'createAt',
          filterValue: this.dateRange[0].toISOString(),
          filterOperator: 3,
        });
      }
      if (this.dateRange[1]) {
        const endDate = new Date(this.dateRange[1]);
        endDate.setHours(23, 59, 59, 999);
        filters.push({
          filterField: 'createAt',
          filterValue: endDate.toISOString(),
          filterOperator: 5,
        });
      }
    }

    await this.regionManager.includeChildInRequest({ regionId: regId }, filters as any);

    const query = {
      offset: this.first,
      take: this.pageSize,
      filters: filters,
      sortBy: 'createAt',
      sort: SortType.NUMBER_1,
    };

    forkJoin({
      data: this.aiFieldService.aiInferenceAiFieldBoundaryGetBoundariesCustomPost(query).pipe(catchError(() => of([]))),
      count: this.aiFieldService
        .aiInferenceAiFieldBoundaryGetCustomGetTotalCountPost({ ...query, offset: 0, take: 0 })
        .pipe(catchError(() => of(0))),
    })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe(({ data, count }) => {
        this.boundaries = this.sortBoundariesByCreateAtDesc(data);
        this.totalCount = count || 0;
      });
  }

  private sortBoundariesByCreateAtDesc(items: AiFieldBoundaryDto[] | null | undefined): AiFieldBoundaryDto[] {
    const list = Array.isArray(items) ? [...items] : [];
    return list.sort((a, b) => {
      const timeA = a.createAt ? new Date(a.createAt).getTime() : 0;
      const timeB = b.createAt ? new Date(b.createAt).getTime() : 0;
      const safeA = Number.isNaN(timeA) ? 0 : timeA;
      const safeB = Number.isNaN(timeB) ? 0 : timeB;
      if (safeB !== safeA) {
        return safeB - safeA;
      }
      return (b.id ?? '').localeCompare(a.id ?? '');
    });
  }

  onPageChange(event: PaginatorState): void {
    this.first = event.first || 0;
    this.loadData();
  }

  onPageSizeChange(newSize: number): void {
    this.pageSize = newSize;
    this.first = 0;
    this.loadData();
  }

  openEditModal(item: AiFieldBoundaryDto): void {
    this.selectedField = item;
    this.displayModal = true;
  }

  onActionCompleted(): void {
    this.displayModal = false;
    this.selectedField = null;
    this.loadData();
  }
}
